<template>
  <div>
    <div class="wrapper">
      <div v-for="(item, firstIndex) in itemsWithoutLast" :key="firstIndex">
        <div class="node-container" :style="{gridTemplateColumns: titleLength}">
          <div class="primary-horizontal-line"></div>
          <div class="primary-arrow-container">
            <z-icon
              v-if="item.children && item.children.length"
              class="icon-arrow"
              @click="item.open = !item.open"
            >
              {{ item.open ? openedIcon : closedIcon }}
            </z-icon>
            <span class="primary-title body-bold">{{ item.title }}</span>
          </div>

          <div class="primary-action">
            <!--@slot Slot referente à ação primária do componente 
                  @binding {object} item Objeto do item primário
            -->
            <slot name="action" :item="item"></slot>
          </div>
        </div>

        <v-expand-transition>
          <template v-if="item.open">
            <div class="secondary-wrapper">
              <div
                v-for="(child, secondIndex) in item.children"
                :key="secondIndex"
              >
                <div
                  class="secondary-node-container"
                  :style="{gridTemplateColumns: childTitleLength}"
                >
                  <div class="secondary-horizontal-line"></div>
                  <div class="secondary-arrow-container">
                    <z-icon
                      v-if="child.children && child.children.length"
                      class="icon-arrow"
                      @click="child.open = !child.open"
                    >
                      {{ child.open ? openedIcon : closedIcon }}
                    </z-icon>

                    <span class="body-text secondary-title">
                      {{ child.title }}
                    </span>
                  </div>

                  <div class="secondary-action">
                    <!--@slot Slot referente à ação secundária do componente 
                         @binding {object} subItem Objeto do item secundário
                         @binding {object} item referente ao item primário
            -->
                    <slot
                      name="sub-action"
                      :item="item"
                      :subItem="child"
                    ></slot>
                  </div>
                </div>

                <v-expand-transition>
                  <template v-if="child.open && child.children">
                    <div class="tertiary-wrapper">
                      <div
                        v-for="(grandChild, thirdIndex) in child.children"
                        :key="thirdIndex"
                      >
                        <div
                          class="tertiary-node-container"
                          :style="{
                            gridTemplateColumns: grandChildTitleLength,
                          }"
                        >
                          <div class="tertiary-horizontal-line"></div>
                          <div class="tertiary-arrow-container">
                            <z-icon
                              v-if="
                                grandChild.children &&
                                grandChild.children.length
                              "
                              class="icon-arrow"
                            >
                              {{ closedIcon }}
                            </z-icon>
                            <span class="body-text tertiary-title">
                              {{ grandChild.title }}</span
                            >
                          </div>
                          <div class="tertiary-action">
                            <!--@slot Slot referente à ação terciária do componente 
                                 @binding {object} item referente ao item primário
                                 @binding {object} subItem referente ao item secundário
                                 @binding {object} grandChild Objeto do item terciário
                            -->
                            <slot
                              name="sub-sub-action"
                              :item="item"
                              :subItem="child"
                              :grandChild="grandChild"
                            ></slot>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </v-expand-transition>
              </div>
            </div>
          </template>
        </v-expand-transition>
      </div>
    </div>

    <last-item
      :last-item="lastItem"
      :title-length="titleLength"
      :child-title-length="childTitleLength"
      :grand-child-title-length="grandChildTitleLength"
      :opened-icon="openedIcon"
      :closed-icon="closedIcon"
    >
      <template #action>
        <!--@slot Slot referente à ação primária do componente 
            @binding {object} item Objeto do item primário
            -->
        <slot name="action" :item="lastItem"></slot>
      </template>

      <template #sub-action="{item, subItem}">
        <!--@slot Slot referente à ação secundária do componente 
            @binding {object} subItem Objeto do item secundário
            @binding {object} item referente ao item primário
            -->
        <slot name="sub-action" :item="item" :subItem="subItem"></slot>
      </template>

      <template #sub-sub-action="{item, subItem, grandChild}">
        <!--@slot Slot referente à ação terciária do componente 
            @binding {object} item referente ao item primário
            @binding {object} subItem referente ao item secundário
            @binding {object} grandChild Objeto do item terciário
            -->
        <slot
          name="sub-sub-action"
          :item="item"
          :subItem="subItem"
          :grandChild="grandChild"
        ></slot>
      </template>
    </last-item>
  </div>
</template>

<script>
import LastItem from './private/LastItem.vue'

/**
 * @displayName Tree View
 */
export default {
  name: 'ZTreeView',
  components: {
    LastItem,
  },
  props: {
    /**
     * Itens a serem consumidos pelo componente
     *
     * @typedef  {title:string,value:any,open:boolean,children:array}
     */
    items: {
      type: Array,
      required: true,
      default: () => [],
    },

    openedIcon: {
      type: String,
      default: '$arrow_down',
    },

    closedIcon: {
      type: String,
      default: '$keyboard_arrow_right_rounded',
    },
  },
  computed: {
    titleLength() {
      let arr = []
      for (const item of this.items) {
        arr.push(item.title.length)
      }

      let bigger = Math.max(...arr)

      return `min-content ${bigger + 25}ch 1fr`
    },

    childTitleLength() {
      let arr = []
      for (const item of this.items) {
        arr.push(item.title.length)
      }

      let bigger = Math.max(...arr)

      return `min-content calc(${bigger + 25}ch - 38px) 1fr`
    },

    grandChildTitleLength() {
      let arr = []

      for (const item of this.items) {
        for (const child of item.children) {
          arr.push(child.title.length)
        }
      }

      let bigger = Math.max(...arr)

      return `min-content calc(${bigger + 25}ch - 160px) 1fr`
    },

    itemsWithoutLast() {
      return this.items.slice(0, -1)
    },

    lastItem() {
      return this.items.slice(-1)[0]
    },
  },
}
</script>

<style lang="scss" scoped>
.wrapper {
  position: relative;
  width: 100%;

  &::before {
    content: '';
    height: calc(100% + 35px);
    position: absolute;
    border-left: dashed 1px #666666;
    left: 0px;
  }

  .node-container {
    display: grid;
    align-items: center;
    margin-top: 8px;
    height: 50px;

    .primary-action {
      display: flex;
      justify-content: flex-end;
      width: 100%;
    }

    .primary-arrow-container {
      display: flex;
      align-items: center;

      .icon-arrow {
        height: min-content;

        &::after {
          height: unset !important;
        }
      }

      .primary-title {
        margin-left: 5px;
      }
    }

    .primary-horizontal-line {
      width: 20px;
      height: 5px;
      border-bottom: dashed 1px #666666;
    }
  }
}

.secondary-wrapper {
  position: relative;
  margin-left: 31px;

  &::before {
    content: '';
    height: calc(100% - 17px);
    position: absolute;
    border-left: dashed 1px #666666;
    left: 0px;
  }

  .secondary-node-container {
    display: grid;
    align-items: center;
    height: 50px;

    .secondary-action {
      display: flex;
      justify-content: flex-end;
      width: 100%;
    }

    .secondary-arrow-container {
      display: flex;
      align-items: center;
    }

    .secondary-title {
      margin-left: 5px;
    }

    .secondary-horizontal-line {
      width: 28px;
      border-bottom: dashed 1px #666666;
    }
  }
}

.tertiary-wrapper {
  position: relative;
  margin-left: 40px;

  &::before {
    content: '';
    height: calc(100% - 17px);
    position: absolute;
    border-left: dashed 1px #666666;
    left: 0px;
  }

  .tertiary-node-container {
    display: grid;
    align-items: center;
    height: 50px;

    .tertiary-action {
      display: flex;
      justify-content: flex-end;
      width: 100%;
    }

    .tertiary-arrow-container {
      display: flex;
      align-items: center;
    }

    .tertiary-title {
      margin-left: 5px;
    }

    .tertiary-horizontal-line {
      width: 28px;
      border-bottom: dashed 1px #666666;
    }
  }
}
</style>
